import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Paper, Grid, Tabs, Tab, 
         Table, TableBody, TableCell, TableHead, TableRow, Checkbox, TextField, MenuItem } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getPipeline } from '../api/pipeline';
import api from '@/services/api';

interface Sequence {
  id: string;
  sequence: string;
  score: number;
  selected: boolean;
}

interface RMSDResult {
  sequence_id: string;
  rmsd: number;
  structure_url?: string;
}

interface Pipeline {
  id: string;
  name: string;
  status: string;
  pipeline_type: string;
  created_at: string;
  params: {
    num_designs: number;
    temperature: number;
    num_to_build: string | number;
    num_structures: number;
  };
  esmif_job_id?: string;
  colabfold_job_id?: string;
  input_pdb: string;
  sequences?: Sequence[];
  results?: {
    rmsd_table?: RMSDResult[];
  };
}

const PipelineDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedSequences, setSelectedSequences] = useState<string[]>([]);
  const [autoSubmit, setAutoSubmit] = useState(false);
  const [numToSubmit, setNumToSubmit] = useState(10);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSelectSequence = (sequenceId: string) => {
    setSelectedSequences(prev => {
      if (prev.includes(sequenceId)) {
        return prev.filter(id => id !== sequenceId);
      } else {
        return [...prev, sequenceId];
      }
    });
  };

  const { data: pipeline, isLoading, error: queryError } = useQuery<Pipeline>({
    queryKey: ['pipeline', id],
    queryFn: () => {
      console.log(`Fetching pipeline ${id}`);
      return getPipeline(id!).then(data => {
        console.log('Pipeline data:', data);
        return data;
      });
    },
    enabled: !!id,
    refetchInterval: (data) => {
      // Refetch every 5 seconds if pipeline is still running
      const shouldRefetch = (data?.status === 'running' || data?.status === 'queued');
      console.log(`Pipeline status: ${data?.status}, refetch: ${shouldRefetch ? 'yes' : 'no'}`);
      return shouldRefetch ? 5000 : false;
    },
  });

  const submitSequencesMutation = useMutation({
    mutationFn: (sequenceIds: string[]) => {
      return api.post(`/api/pipelines/${id}/submit_sequences/`, { sequence_ids: sequenceIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipeline', id] });
    },
    onError: (error) => {
      console.error('Failed to submit sequences:', error);
      setError('Failed to submit sequences for structure prediction');
    }
  });

  const handleSubmitSequences = useCallback((sequenceIds?: string[]) => {
    const idsToSubmit = sequenceIds || selectedSequences;
    if (idsToSubmit.length === 0) {
      setError('Please select at least one sequence');
      return;
    }
    submitSequencesMutation.mutate(idsToSubmit);
  }, [selectedSequences, submitSequencesMutation, setError]);

  useEffect(() => {
    if (queryError) {
      setError('Failed to load pipeline details');
    }
  }, [queryError]);

  // Auto-select top sequences when they become available
  useEffect(() => {
    // Only run this effect if autoSubmit is true, pipeline has sequences, and is in sequence_review state
    if (pipeline?.sequences && autoSubmit && pipeline.status === 'sequence_review' && !submitSequencesMutation.isLoading) {
      if (Array.isArray(pipeline.sequences)) {
        console.log("Auto-submitting top sequences...");
        const sorted = [...pipeline.sequences].sort((a, b) => a.score - b.score);
        const topIds = sorted.slice(0, numToSubmit).map(seq => seq.id);
        
        // Reset autoSubmit to prevent multiple submissions
        setAutoSubmit(false);
        
        // Submit the sequences
        handleSubmitSequences(topIds);
      }
    }
    
    // If pipeline status changes to 'running', reset autoSubmit
    if (pipeline?.status !== 'sequence_review') {
      setAutoSubmit(false);
    }
  }, [pipeline?.status, autoSubmit, numToSubmit, handleSubmitSequences, pipeline?.sequences, submitSequencesMutation.isLoading]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !pipeline) {
    return (
      <Box p={3}>
        <Typography color="error">{error || 'Pipeline not found'}</Typography>
        <Button variant="contained" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  // Safely check if sequences are available and are an array
  const hasSequences = pipeline.sequences && Array.isArray(pipeline.sequences) && pipeline.sequences.length > 0;
  // Safely check if results are available
  const hasResults = pipeline.results?.rmsd_table && Array.isArray(pipeline.results.rmsd_table) && pipeline.results.rmsd_table.length > 0;

  return (
    <Box p={3}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom>
              {pipeline.name}
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              Status: {pipeline.status}
            </Typography>
            <Typography variant="body2">
              Created: {new Date(pipeline.created_at).toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6">Pipeline Parameters</Typography>
            <Typography>Number of Sequences to Generate: {pipeline.params.num_designs}</Typography>
            <Typography>Temperature: {pipeline.params.temperature}</Typography>
            <Typography>Number to Build: {pipeline.params.num_to_build}</Typography>
            <Typography>Number of Structures per Sequence: {pipeline.params.num_structures}</Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Overview" />
          <Tab label="Sequences" disabled={!hasSequences} />
          <Tab label="Results" disabled={!hasResults} />
        </Tabs>

        {tabValue === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>Pipeline Progress</Typography>
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1">
                1. ESM-IF Sequence Generation:
                {' '}
                {pipeline.esmif_job_id ? (
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => navigate(`/esmif/jobs/${pipeline.esmif_job_id}`)}
                  >
                    View Job
                  </Button>
                ) : 'Not started'}
              </Typography>
            </Paper>
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1">
                2. ColabFold Structure Prediction:
                {' '}
                {pipeline.colabfold_job_id ? (
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => navigate(`/colabfold/jobs/${pipeline.colabfold_job_id}`)}
                  >
                    View Job
                  </Button>
                ) : 'Not started'}
              </Typography>
            </Paper>
          </Box>
        )}

        {tabValue === 1 && hasSequences && (
          <Box>
            <Typography variant="h6" gutterBottom>Generated Sequences</Typography>
            
            {pipeline.status === 'sequence_review' && (
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={() => handleSubmitSequences()}
                  disabled={selectedSequences.length === 0 || submitSequencesMutation.isLoading}
                >
                  {submitSequencesMutation.isLoading ? 'Submitting...' : 'Submit Selected Sequences'}
                </Button>
                
                <Typography variant="body2" sx={{ ml: 2 }}>
                  {selectedSequences.length} sequences selected
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                  <Typography sx={{ mr: 2 }}>Auto-submit top:</Typography>
                  <TextField
                    select
                    size="small"
                    value={numToSubmit}
                    onChange={(e) => setNumToSubmit(Number(e.target.value))}
                    sx={{ width: 100, mr: 2 }}
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={30}>30</MenuItem>
                  </TextField>
                  <Button 
                    variant="outlined"
                    disabled={submitSequencesMutation.isLoading}
                    onClick={() => {
                      setAutoSubmit(true);
                      if (pipeline.sequences && Array.isArray(pipeline.sequences)) {
                        const sequences = pipeline.sequences;
                        const sorted = [...sequences].sort((a, b) => a.score - b.score);
                        const topIds = sorted.slice(0, numToSubmit).map(seq => seq.id);
                        handleSubmitSequences(topIds);
                      }
                    }}
                  >
                    {submitSequencesMutation.isLoading ? 'Submitting...' : 'Auto-submit Top Sequences'}
                  </Button>
                </Box>
              </Box>
            )}
            
            <Table size="small">
              <TableHead>
                <TableRow>
                  {pipeline.status === 'sequence_review' && <TableCell>Select</TableCell>}
                  <TableCell>ID</TableCell>
                  <TableCell>Sequence</TableCell>
                  <TableCell>Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(pipeline.sequences) && pipeline.sequences
                  .sort((a, b) => a.score - b.score)
                  .map((seq) => (
                    <TableRow key={seq.id}>
                      {pipeline.status === 'sequence_review' && (
                        <TableCell>
                          <Checkbox 
                            checked={selectedSequences.includes(seq.id)}
                            onChange={() => handleSelectSequence(seq.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>{seq.id}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {seq.sequence.length > 50 
                          ? `${seq.sequence.substring(0, 50)}...` 
                          : seq.sequence}
                      </TableCell>
                      <TableCell>{seq.score.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {tabValue === 2 && hasResults && (
          <Box>
            <Typography variant="h6" gutterBottom>RMSD Results</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sequence ID</TableCell>
                  <TableCell>RMSD</TableCell>
                  <TableCell>Structure</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pipeline.results?.rmsd_table && pipeline.results.rmsd_table.map((result, index) => {
                  // Debug log for each result
                  console.log(`Result ${index}:`, result);
                  return (
                    <TableRow key={index}>
                      <TableCell>{result.sequence_id}</TableCell>
                      <TableCell>{result.rmsd.toFixed(2)}</TableCell>
                      <TableCell>
                        <Button 
                          size="small" 
                          variant="outlined"
                          onClick={() => {
                            if (result.structure_url) {
                              console.log('Opening URL:', result.structure_url);
                              window.open(result.structure_url, '_blank');
                            } else {
                              console.log('No structure URL available, redirecting to ColabFold job page');
                              // Fallback to the ColabFold job page
                              if (pipeline.colabfold_job_id) {
                                navigate(`/colabfold/jobs/${pipeline.colabfold_job_id}`);
                              }
                            }
                          }}
                          disabled={!result.structure_url && !pipeline.colabfold_job_id}
                        >
                          {result.structure_url ? 'View Structure' : 'View Job'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          <Button variant="contained" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default PipelineDetail; 